import {queryOptions, useMutation, useQuery, useQueryClient, type QueryClient} from '@tanstack/react-query';
import {api} from './client';

// Перенос файлов по ссылке на Google Диск (контракт §2.1–§2.5, пути страницы §3).
// Типы повторяют ответы apps/api/src/routes/linkImports.ts; дублируем свободно,
// как и в остальных api-модулях: фронт не импортирует типы бэкенда.

export type LinkImportResource = 'custom-requests' | 'content-requests';

export type LinkImportKind = 'video' | 'image';
export type LinkImportItemStatus = 'queued' | 'running' | 'done' | 'failed';
export type LinkImportItemPhase =
  | 'queued'
  | 'waiting_disk'
  | 'copying'
  | 'downloading'
  | 'compressing'
  | 'uploading'
  | 'done'
  | 'failed';
export type LinkImportOutcome = 'copied' | 'compressed' | 'original';
export type LinkImportItemError =
  | 'no_access'
  | 'download_disabled'
  | 'source_gone'
  | 'drive'
  | 'quota'
  | 'internal';
export type LinkImportSkipReason =
  | 'subfolder'
  | 'shortcut'
  | 'google_doc'
  | 'not_media'
  | 'trashed'
  | 'download_disabled';

export interface LinkImportItem {
  id: string;
  batchId: string;
  name: string;
  sizeBytes: number;
  kind: LinkImportKind;
  status: LinkImportItemStatus;
  phase: LinkImportItemPhase;
  progress: number | null;
  outcome: LinkImportOutcome | null;
  error: LinkImportItemError | null;
}

export interface LinkImportList {
  active: boolean;
  failed: number;
  items: LinkImportItem[];
}

export interface LinkImportPreviewItem {
  sourceFileId: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  kind: LinkImportKind;
  plan: 'copy' | 'probe';
  already: boolean;
}

export interface LinkImportPreview {
  source: {kind: 'file' | 'folder'; name: string};
  items: LinkImportPreviewItem[];
  skipped: {name: string; reason: LinkImportSkipReason}[];
  // Только по `already: false`.
  totals: {count: number; bytes: number};
}

// Показанный предпросмотр вместе с ключом идемпотентности: ключ один на предпросмотр,
// поэтому повторное «Перенести» (после обрыва связи) не заводит второй перенос.
export interface ShownLinkPreview {
  url: string;
  idempotencyKey: string;
  preview: LinkImportPreview;
}

export interface StartedLinkImport {
  batchId: string;
  items: LinkImportItem[];
}

// Пока идёт перенос — смотрим часто, чтобы проценты двигались; иначе редко: только
// чтобы подхватить перенос, запущенный из приложения.
const ACTIVE_POLL_MS = 4_000;
const IDLE_POLL_MS = 30_000;

export const linkImportKeys = {
  items: (resource: LinkImportResource, requestId: string) => ['link-imports', resource, requestId] as const,
};

function linkImportsPath(resource: LinkImportResource, requestId: string): string {
  return `/v1/${resource}/${encodeURIComponent(requestId)}/link-imports`;
}

function bearer(token: string): Record<string, string> {
  return {Authorization: `Bearer ${token}`};
}

// Не apiJson: он кладёт `...init` поверх своих заголовков, и при переданном
// Authorization пропадает Content-Type — express.json тогда не разберёт тело.
function jsonHeaders(token: string): Record<string, string> {
  return {...bearer(token), 'Content-Type': 'application/json'};
}

export function linkImportItemsQueryOptions(resource: LinkImportResource, requestId: string, token: string) {
  return queryOptions({
    queryKey: linkImportKeys.items(resource, requestId),
    queryFn: () => api<LinkImportList>(linkImportsPath(resource, requestId), {headers: bearer(token)}),
    refetchInterval: (query) => (query.state.data?.active ? ACTIVE_POLL_MS : IDLE_POLL_MS),
    // Вернулись на вкладку — сразу свежий ход, а не ждать следующего тика.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useLinkImportItems(resource: LinkImportResource, requestId: string, token: string) {
  return useQuery(linkImportItemsQueryOptions(resource, requestId, token));
}

// Число неубранных ошибок для страницы (окно перед сдачей). Сам запрос не делает
// и интервала не заводит: подписывается на кэш, который ведёт блок переноса.
export function useLinkImportFailedCount(
  resource: LinkImportResource,
  requestId: string,
  token: string,
): number {
  const {data} = useQuery({
    ...linkImportItemsQueryOptions(resource, requestId, token),
    enabled: false,
    select: (list) => list.failed,
  });
  return data ?? 0;
}

export function invalidateLinkImportItems(qc: QueryClient, resource: LinkImportResource, requestId: string) {
  return qc.invalidateQueries({queryKey: linkImportKeys.items(resource, requestId)});
}

export function usePreviewLink(resource: LinkImportResource, requestId: string, token: string) {
  return useMutation({
    mutationFn: async (url: string): Promise<ShownLinkPreview> => {
      const preview = await api<LinkImportPreview>(`${linkImportsPath(resource, requestId)}/preview`, {
        method: 'POST',
        headers: jsonHeaders(token),
        body: JSON.stringify({url}),
      });
      return {url, preview, idempotencyKey: crypto.randomUUID()};
    },
  });
}

export function useStartImport(resource: LinkImportResource, requestId: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({url, idempotencyKey}: {url: string; idempotencyKey: string}) =>
      api<StartedLinkImport>(linkImportsPath(resource, requestId), {
        method: 'POST',
        headers: {...jsonHeaders(token), 'Idempotency-Key': idempotencyKey},
        body: JSON.stringify({url}),
      }),
    onSuccess: (started) => {
      // Строки видны сразу, и «идёт перенос» (а с ним запрет сдачи) включается
      // без ожидания следующего опроса.
      qc.setQueryData<LinkImportList>(linkImportKeys.items(resource, requestId), (old) => {
        const startedIds = new Set(started.items.map((item) => item.id));
        const items = [...(old?.items ?? []).filter((item) => !startedIds.has(item.id)), ...started.items];
        return {
          active: items.some((item) => item.status === 'queued' || item.status === 'running'),
          failed: old?.failed ?? 0,
          items,
        };
      });
    },
    onSettled: () => {
      void invalidateLinkImportItems(qc, resource, requestId);
    },
  });
}

export function useRetryItem(resource: LinkImportResource, requestId: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api<LinkImportItem>(`${linkImportsPath(resource, requestId)}/items/${encodeURIComponent(itemId)}/retry`, {
        method: 'POST',
        headers: bearer(token),
      }),
    onSettled: () => {
      void invalidateLinkImportItems(qc, resource, requestId);
    },
  });
}

export function useDismissItem(resource: LinkImportResource, requestId: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      api<void>(`${linkImportsPath(resource, requestId)}/items/${encodeURIComponent(itemId)}`, {
        method: 'DELETE',
        headers: bearer(token),
      }),
    onSettled: () => {
      void invalidateLinkImportItems(qc, resource, requestId);
    },
  });
}
